import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Notifications as BellIcon,
  ReportProblem as CriticalIcon,
  AssignmentInd as AssignedIcon,
  FolderShared as CaseAssignedIcon,
  Comment as CommentIcon,
  TaskAlt as TaskIcon,
  Timer as SlaIcon,
  PersonAdd as InviteIcon,
  Info as InfoIcon,
  DoneAll as DoneAllIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';

const POLL_MS = 60000;

const TYPE_ICONS = {
  critical_detection: <CriticalIcon color="error" />,
  detection_assigned: <AssignedIcon color="primary" />,
  case_assigned: <CaseAssignedIcon color="primary" />,
  case_comment: <CommentIcon color="action" />,
  detection_comment: <CommentIcon color="action" />,
  task_assigned: <TaskIcon color="secondary" />,
  sla_breach: <SlaIcon color="warning" />,
  invitation_accepted: <InviteIcon color="success" />,
};

const typeIcon = (type) => TYPE_ICONS[type] || <InfoIcon color="action" />;

/** Convert an absolute app link (or a path) into a router path. */
const toPath = (link) => {
  if (!link) return null;
  try {
    const url = new URL(link, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch (e) {
    return link.startsWith('/') ? link : null;
  }
};

const timeAgo = (value) => {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return '';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(value).toLocaleDateString();
};

const NotificationRow = ({ n, onClick, onDelete }) => (
  <ListItemButton onClick={() => onClick(n)} alignItems="flex-start" sx={{ bgcolor: n.read ? 'transparent' : 'action.hover' }}>
    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{typeIcon(n.type)}</ListItemIcon>
    <ListItemText
      primary={n.title}
      secondary={
        <>
          <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block' }}>
            {n.message}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary">
            {timeAgo(n.createdAt)}
          </Typography>
        </>
      }
      primaryTypographyProps={{ fontWeight: n.read ? 400 : 700, variant: 'body2' }}
    />
    {onDelete && (
      <Tooltip title="Delete">
        <IconButton size="small" edge="end" onClick={(e) => { e.stopPropagation(); onDelete(n); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </ListItemButton>
);

const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // "View all" dialog
  const [allOpen, setAllOpen] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [allPage, setAllPage] = useState(1);
  const [allPages, setAllPages] = useState(1);
  const [allLoading, setAllLoading] = useState(false);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.notifications.getAll({ limit: 15 });
      if (!mounted.current) return;
      setItems(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (e) {
      // Silent: the bell should never break the app bar
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const loadAll = useCallback(async (page) => {
    try {
      setAllLoading(true);
      const res = await apiService.notifications.getAll({ limit: 20, page });
      if (!mounted.current) return;
      setAllItems(res.data.notifications || []);
      setAllPages(res.data.pagination?.pages || 1);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (e) {
      // ignore
    } finally {
      if (mounted.current) setAllLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allOpen) loadAll(allPage);
  }, [allOpen, allPage, loadAll]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    load();
  };
  const handleClose = () => setAnchorEl(null);

  const handleClick = async (n) => {
    if (!n.read) {
      try {
        await apiService.notifications.markRead(n._id);
      } catch (e) {
        // ignore
      }
      const mark = (list) => list.map((x) => (x._id === n._id ? { ...x, read: true } : x));
      setItems(mark);
      setAllItems(mark);
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    const path = toPath(n.link);
    handleClose();
    setAllOpen(false);
    if (path) navigate(path);
  };

  const handleMarkAll = async () => {
    try {
      await apiService.notifications.markAllRead();
      const mark = (list) => list.map((x) => ({ ...x, read: true }));
      setItems(mark);
      setAllItems(mark);
      setUnreadCount(0);
    } catch (e) {
      // ignore
    }
  };

  const handleDelete = async (n) => {
    try {
      await apiService.notifications.delete(n._id);
      setItems((list) => list.filter((x) => x._id !== n._id));
      setAllItems((list) => list.filter((x) => x._id !== n._id));
      if (!n.read) setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      // ignore
    }
  };

  const openAll = () => {
    handleClose();
    setAllPage(1);
    setAllOpen(true);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleOpen} aria-label="notifications" sx={{ mr: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: '95vw' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications {unreadCount > 0 && `(${unreadCount})`}
          </Typography>
          <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAll} disabled={unreadCount === 0}>
            Mark all read
          </Button>
        </Box>
        <Divider />
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <BellIcon color="disabled" sx={{ fontSize: 40 }} />
            <Typography variant="body2" color="text.secondary">You're all caught up.</Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {items.map((n) => (
              <NotificationRow key={n._id} n={n} onClick={handleClick} />
            ))}
          </List>
        )}
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <Button size="small" onClick={openAll}>View all</Button>
        </Box>
      </Popover>

      <Dialog open={allOpen} onClose={() => setAllOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          All notifications
          <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAll} disabled={unreadCount === 0}>
            Mark all read
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {allLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : allItems.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No notifications yet.</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {allItems.map((n) => (
                <NotificationRow key={n._id} n={n} onClick={handleClick} onDelete={handleDelete} />
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Pagination count={allPages} page={allPage} onChange={(e, p) => setAllPage(p)} size="small" />
          <Button onClick={() => setAllOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationBell;
